// src/components/NextEngineConnectModal.tsx
//
// 原価設定ページから開くネクストエンジン連携モーダル。
// NE 商品マスタは企業単位のため「企業として1回連携」する。連携した原価は、その企業
// (account)に紐づく全モール店舗(楽天/Amazon)へ自動でファンアウト反映される。
//
// このモーダルは「連携する / 原価を取得する」までを担当する。取得は数分かかるため、
// 開始したらモーダルは閉じ、経過と結果は原価一覧の画面(バナー)で確認できるようにする
// (ずっとモーダルを開いたままにしない)。連携(NE ログイン)は別タブで開き、この画面は
// そのまま残す。
// アクセシビリティ: role="dialog" / aria-modal / Esc で閉じる / 開いたら閉じるボタンへ
// フォーカス / メッセージは role="alert" で読み上げ。

import React, { useEffect, useRef, useState } from "react";
import { apiDeleteJson, apiGetJson, apiPostJson } from "../utils/apiClient";

type NeStatus = {
    ok: boolean;
    configured: boolean;
    connected: boolean;
    connectionId: string;
    companyName: string | null;
    companyId: string | null;
    activeJobId: string | null;
    lastSyncedAt: string | null;
};

// 重要度は色ではなく形状で区別する(色覚・ロービジョン・印刷対応)。
// プライマリ=塗り / セカンダリ=アウトライン(太線) / ターシャリ=テキスト。
const BTN_BASE =
    "inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1";
const BTN_PRIMARY = `${BTN_BASE} font-semibold bg-blue-600 text-white hover:bg-blue-700`;
const BTN_SECONDARY = `${BTN_BASE} font-medium border-2 border-blue-600 bg-white text-blue-700 hover:bg-blue-50`;
const BTN_TERTIARY_DANGER = `${BTN_BASE} font-medium bg-transparent text-red-700 hover:bg-red-50 hover:underline focus-visible:ring-red-400`;

// ネクストエンジンのアプリストア(利益Naviの詳細ページ)。ここで利用開始しないと、
// 連携時にネクストエンジン側が「アプリの連携に失敗しました」を返す。
const NE_APP_STORE_URL = "https://base.next-engine.org/apps/8118/detail/";

function formatDateTime(iso: string | null): string {
    if (!iso) return "まだ取得していません";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "まだ取得していません";
    return d.toLocaleString("ja-JP", { hour12: false });
}

// 会社名(ネクストエンジンの会社ID)の表記。ID は NE 画面に出る数字の会社ID
// (company_ne_id)のみ併記する。古い連携で保存されたハッシュ(company_id)は
// 利用者が見る会社IDと一致しないため出さない(数字以外は会社名のみ表示)。
function companyLabel(name: string | null, id: string | null): string {
    const n = name && name.trim() ? name.trim() : "（会社名を取得できませんでした）";
    const cid = id ? String(id).trim() : "";
    return /^\d+$/.test(cid) ? `${n}（${cid}）` : n;
}

export default function NextEngineConnectModal({
    open,
    onClose,
    onJobStarted,
    onActiveJob,
    jobRunning,
}: {
    open: boolean;
    onClose: () => void;
    // 取得を開始した(新しい job_id)。ページ側でポーリング開始＋このモーダルを閉じる。
    onJobStarted: (jobId: string) => void;
    // 既に実行中のジョブを見つけた。ページ側でポーリングに合流する(モーダルは閉じない)。
    onActiveJob: (jobId: string) => void;
    // ページ側で取得中かどうか。取得中は二重起動を防ぐためボタンを無効化する。
    jobRunning: boolean;
}) {
    const [status, setStatus] = useState<NeStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const closeRef = useRef<HTMLButtonElement>(null);

    const loadStatus = async () => {
        setLoading(true);
        try {
            const s = await apiGetJson<NeStatus>("/api/nextengine/status");
            setStatus(s);
            // 既に実行中のジョブがあればページ側のポーリングに合流する。
            if (s?.connected && s.activeJobId) onActiveJob(s.activeJobId);
        } catch {
            setError("連携の状態を確認できませんでした。時間をおいて開き直してください。");
        } finally {
            setLoading(false);
        }
    };

    // 開いたときに状態取得。
    useEffect(() => {
        if (!open) return;
        setError(null);
        void loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // 別タブでの連携(NE ログイン)から戻ってきたら状態を取り直す。
    useEffect(() => {
        if (!open) return;
        const onFocus = () => void loadStatus();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Esc で閉じる + 開いたら閉じるボタンへフォーカス
    useEffect(() => {
        if (!open) return;
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const connected = Boolean(status?.connected);
    const configured = Boolean(status?.configured);

    // 連携(NE ログイン)は別タブで開く。ポップアップブロックを避けるため、クリック
    // 直後に空タブを同期的に開き、authUrl が取れてから遷移先を差し込む。
    const handleConnect = async () => {
        setBusy(true);
        setError(null);
        const authTab = window.open("", "_blank");
        try {
            const res = await apiPostJson<{ ok: boolean; authUrl?: string }>("/api/nextengine/connect");
            if (res.authUrl) {
                if (authTab) authTab.location.href = res.authUrl;
                else window.location.href = res.authUrl; // ブロックされた場合は同タブ遷移
                return;
            }
            authTab?.close();
            setError("連携を開始できませんでした。時間をおいてもう一度お試しください。");
        } catch {
            authTab?.close();
            setError("連携を開始できませんでした。時間をおいてもう一度お試しください。");
        } finally {
            setBusy(false);
        }
    };

    const handleSync = async () => {
        setBusy(true);
        setError(null);
        try {
            const res = await apiPostJson<{ ok: boolean; job_id?: string }>(
                "/api/nextengine/jobs/sync-item-costs",
            );
            if (res.job_id) {
                onJobStarted(res.job_id); // ページ側でポーリング開始＋モーダルを閉じる
            } else {
                setError("原価の取得を開始できませんでした。時間をおいてもう一度お試しください。");
            }
        } catch {
            setError("原価の取得を開始できませんでした。連携が有効かをご確認のうえ、もう一度お試しください。");
        } finally {
            setBusy(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm("ネクストエンジンとの連携を解除しますか？")) return;
        setBusy(true);
        setError(null);
        try {
            await apiDeleteJson("/api/nextengine/connection");
            await loadStatus();
        } catch {
            setError("連携を解除できませんでした。時間をおいてもう一度お試しください。");
        } finally {
            setBusy(false);
        }
    };

    const disabled = busy || jobRunning;

    return (
        <div
            className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="ne-modal-title"
                className="w-full max-w-md rounded-xl bg-white shadow-lg"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 id="ne-modal-title" className="text-sm font-semibold text-slate-900">
                        ネクストエンジン連携
                    </h2>
                    <button
                        ref={closeRef}
                        type="button"
                        onClick={onClose}
                        aria-label="閉じる"
                        className="rounded p-1 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    >
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M6.3 4.9 4.9 6.3 8.6 10l-3.7 3.7 1.4 1.4L10 11.4l3.7 3.7 1.4-1.4L11.4 10l3.7-3.7-1.4-1.4L10 8.6 6.3 4.9z" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-3 px-4 py-4 text-sm text-slate-900">
                    {loading ? (
                        <p className="text-slate-700">読み込み中…</p>
                    ) : (
                        <>
                            {connected ? (
                                <dl className="space-y-1 text-sm">
                                    <div className="flex gap-2">
                                        <dt className="w-24 shrink-0 text-slate-700">連携中の企業</dt>
                                        <dd className="text-slate-900">
                                            {companyLabel(status?.companyName ?? null, status?.companyId ?? null)}
                                        </dd>
                                    </div>
                                    <div className="flex gap-2">
                                        <dt className="w-24 shrink-0 text-slate-700">前回の取得</dt>
                                        <dd className="text-slate-900">{formatDateTime(status?.lastSyncedAt ?? null)}</dd>
                                    </div>
                                </dl>
                            ) : (
                                <p className="text-sm text-slate-800">まだネクストエンジンと連携していません。</p>
                            )}

                            {!configured && (
                                <p role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                    この環境ではネクストエンジン連携をご利用いただけません（設定が未完了です）。
                                </p>
                            )}
                            {error && (
                                <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
                                    {error}
                                </p>
                            )}

                            {jobRunning && (
                                <p role="status" className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-800">
                                    原価の取得を受け付けました。状況は原価一覧の画面に表示されます。
                                </p>
                            )}

                            <p className="text-xs text-slate-700">
                                ネクストエンジンに登録した商品原価を、この企業のすべての店舗の当月分に取り込みます。
                            </p>

                            {!connected && (
                                <p className="text-xs text-slate-700">
                                    はじめて連携するときは、ネクストエンジンのアプリストアで「利益Navi for ネクストエンジンかんたん連携」の利用開始が必要です。
                                    <span className="font-medium text-slate-900">利益Naviのご契約とは別に、料金がかかります。</span>
                                    <a
                                        href={NE_APP_STORE_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-1 text-slate-800 underline underline-offset-2 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                                    >
                                        アプリストアを開く
                                    </a>
                                </p>
                            )}

                            {/*
                              重要度は色でなく形状で区別:
                                プライマリ=塗り / セカンダリ=アウトライン / ターシャリ=テキスト
                              DOM順(=フォーカス順)= セカンダリ→プライマリ→ターシャリ
                            */}
                            <div className="flex flex-col gap-2 pt-1">
                                {!connected ? (
                                    <button
                                        type="button"
                                        onClick={handleConnect}
                                        disabled={busy || !configured}
                                        className={`${BTN_PRIMARY} w-full`}
                                    >
                                        ネクストエンジンと連携する
                                    </button>
                                ) : (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleConnect}
                                            disabled={disabled}
                                            className={`${BTN_SECONDARY} w-full`}
                                        >
                                            連携をやり直す
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSync}
                                            disabled={disabled}
                                            className={`${BTN_PRIMARY} w-full`}
                                        >
                                            {jobRunning ? "取得中…" : "原価を取得"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleDisconnect}
                                            disabled={disabled}
                                            className={`${BTN_TERTIARY_DANGER} w-full`}
                                        >
                                            連携を解除
                                        </button>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
